import * as yup from "yup"
import { DateTime } from "luxon"
import { Form, Formik } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box } from "@mui/material"
import { isNotInPastIfToday, isWithinOpeningHours, prepareInitialValues, validateDeliveryDate } from "./helpers"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { ProductWithQuantity, SelectedPriceListItem, CreateOrderDialogFlow } from "../../../createOrderTypes"
import { PopoverDeliveryTimeFormContent } from "./components/form/PopoverDeliveryTimeFormContent"
import { DeliveryTimesField, DeliveryWithCart, DeliveryTimeDay } from "../../form/formTypes"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    subtitle: {
        color: theme.palette.tailwind.slate[950],
    },
    content: {
        padding: "2rem 4rem",
        display: "flex",
        gap: "2rem",
        flexDirection: "column",
        color: theme.palette.tailwind.slate[950],
        width: "31rem",
    },
}))

export type DeliveryTimeValues = Readonly<{
    time: DateTime | null
    orderLines: ProductWithQuantity[]
}>

export type CreateUpdateDeliveryFormValues = Readonly<{
    deliveryDate: DateTime | null
    deliveryTimes: DeliveryTimeValues[]
}>

type CreateUpdateDeliveryTimePopoverProps = Readonly<{
    initialValues?: DeliveryTimesField
    eventStartTime: DateTime | null
    isDisabledDatePicker: boolean
    priceList: SelectedPriceListItem
    onSubmit: (deliveries: DeliveryTimesField) => void
    flowConfig?: CreateOrderDialogFlow
    allowedWeekdays: number[]
    closingDates: string[]
    openingHours: DeliveryTimeDay[]
    maxAllowedDeliveries: number
    handlePopoverClose: () => void
}>

export function CreateUpdateDeliveryTimePopover({
    initialValues,
    eventStartTime,
    isDisabledDatePicker,
    priceList,
    onSubmit,
    flowConfig,
    allowedWeekdays,
    closingDates,
    openingHours = [],
    maxAllowedDeliveries,
    handlePopoverClose,
}: CreateUpdateDeliveryTimePopoverProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const validationSchema = yup.object({
        deliveryDate: yup
            .mixed<DateTime>()
            .nullable()
            .required(l10n.getString("required-field"))
            .test(
                "delivery-date-allowed",
                l10n.getString("create-order-delivery-times-dialog-delivery-date-close-tooltip"),
                (value) => validateDeliveryDate(value, allowedWeekdays, closingDates).isValidDeliveryDate,
            ),

        deliveryTimes: yup
            .array()
            .of(
                yup.object({
                    time: yup
                        .mixed<DateTime>()
                        .nullable()
                        .required(l10n.getString("required-field"))
                        .test(
                            "within-opening-hours",
                            l10n.getString("create-order-delivery-times-opening-hours-error"),
                            function (value) {
                                const parent = this.from?.[1]?.value as CreateUpdateDeliveryFormValues
                                const deliveryDate = parent?.deliveryDate

                                if (!deliveryDate || !value) return true

                                return isWithinOpeningHours(value, deliveryDate, openingHours)
                            },
                        )
                        .test(
                            "not-in-past-if-today",
                            l10n.getString("create-order-delivery-times-past-time-error"),
                            function (value) {
                                const parent = this.from?.[1]?.value as CreateUpdateDeliveryFormValues
                                const deliveryDate = parent?.deliveryDate
                                if (!deliveryDate || !value) return true

                                return isNotInPastIfToday(value, deliveryDate, dateTimeFactory)
                            },
                        ),
                }),
            )
            .test(
                "unique-times",
                l10n.getString("create-order-delivery-times-same-time-error"),
                function (deliveryTimes) {
                    if (!deliveryTimes || deliveryTimes.length === 0) return true

                    const formatted = deliveryTimes.map((t) => (t.time?.toFormat ? t.time.toFormat("HH:mm") : null))
                    const seen = new Set()

                    for (let i = 0; i < formatted.length; i++) {
                        if (formatted[i] && seen.has(formatted[i])) {
                            return this.createError({
                                path: `deliveryTimes[${i}].time`,
                                message: l10n.getString("create-order-delivery-times-same-time-error"),
                            })
                        }
                        seen.add(formatted[i]!)
                    }

                    return true
                },
            ),
    })

    const handleSubmit = (values: CreateUpdateDeliveryFormValues) => {
        const formattedDate = values.deliveryDate?.toFormat("yyyy-MM-dd")
        const formattedTimes: DeliveryWithCart[] = values.deliveryTimes.map((entry) => ({
            time: entry.time?.toFormat("HH:mm") ?? "",
            orderLines: entry.orderLines,
        }))
        formattedDate &&
            onSubmit({
                deliveryDate: formattedDate,
                deliveryTimes: formattedTimes,
                activeTime: formattedTimes[0].time ?? null,
            })
    }

    return (
        <Box className={classes.content}>
            <Formik<CreateUpdateDeliveryFormValues>
                enableReinitialize
                initialValues={prepareInitialValues(dateTimeFactory, eventStartTime, openingHours, initialValues)}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                <Form>
                    <PopoverDeliveryTimeFormContent
                        priceList={priceList}
                        flowConfig={flowConfig}
                        allowedWeekdays={allowedWeekdays}
                        eventStartTime={eventStartTime}
                        isDisabledDatePicker={isDisabledDatePicker}
                        closingDates={closingDates}
                        openingHours={openingHours}
                        maxAllowedDeliveries={maxAllowedDeliveries}
                        handlePopoverClose={handlePopoverClose}
                    />
                </Form>
            </Formik>
        </Box>
    )
}
